import React, { useState, useRef, useEffect } from 'react'
import styles from './Header.module.scss'
import { profileActions } from '../../store/profileReducer'
import { uiActions } from '../../store/uiReducer'
import { useDispatch, useSelector } from '../../store'
import { NavLink, useNavigate } from 'react-router-dom'
import { Moon, Sun, LogOut, User, Key, ChevronDown, ExternalLink, Terminal } from 'lucide-react'
import api from '../../api'
import { useClickOutside } from '../../hooks/useClickOutside'
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal'
import { config } from '../../config'

const MONITOR_API_URL = config.MONITOR_API_URL

const Header = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useSelector((state) => state.ui.theme)
  const user = useSelector((state) => state.profile.user)
  const [logoutMutation] = api.useLogoutMutation()

  const [showDropdown, setShowDropdown] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [workerStatus, setWorkerStatus] = useState<{
    status: 'idle' | 'busy' | 'down'
    queue: { messages: number; consumers: number }
    dbCheckQueue: { messages: number; consumers: number }
    lastTaskStatus: string | null
  }>({
    status: 'idle',
    queue: { messages: 0, consumers: 0 },
    dbCheckQueue: { messages: 0, consumers: 0 },
    lastTaskStatus: null,
  })
  const dropdownRef = useRef<HTMLDivElement>(null)

  useClickOutside(() => setShowDropdown(false), dropdownRef)

  // Poll worker status every 3 seconds
  useEffect(() => {
    const fetchWorkerStatus = async () => {
      try {
        const res = await fetch(`${MONITOR_API_URL}/worker-status`)
        const data = await res.json()
        // Override status to down if last task is ERROR
        let status = data.status
        if (data.task?.status === 'ERROR') {
          status = 'down'
        }
        setWorkerStatus({
          status,
          queue: data.queue || { messages: 0, consumers: 0 },
          dbCheckQueue: data.dbCheckQueue || { messages: 0, consumers: 0 },
          lastTaskStatus: data.task?.status || null,
        })
      } catch {
        setWorkerStatus({
          status: 'down',
          queue: { messages: 0, consumers: 0 },
          dbCheckQueue: { messages: 0, consumers: 0 },
          lastTaskStatus: null,
        })
      }
    }
    fetchWorkerStatus()
    const interval = setInterval(fetchWorkerStatus, 3000)
    return () => clearInterval(interval)
  }, [])

  const statusLabels = { idle: 'ready', busy: 'processing', down: 'offline' }

  const onLogout = async () => {
    try {
      await logoutMutation().unwrap()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Explicitly clear state and navigate to ensure immediate UI update
      dispatch(profileActions.logout())
      localStorage.removeItem('persist:root')
      navigate('/login')
    }
  }

  const toggleTheme = () => {
    dispatch(uiActions.toggleTheme())
  }

  const navItems = [
    { to: '/worker_tasks', label: 'Tasks' },
    { to: '/exceptions', label: 'Exceptions' },
    { to: '/replacement_rules', label: 'Rules' },
    { to: '/exclusion_rule', label: 'Exclusions' },
    { to: '/price_types', label: 'Prices' },
    { to: '/makes_list', label: 'Makes' },
    { to: '/models_list', label: 'Models' },
  ]

  const monitorItems = [
    { to: '/feed_monitor', label: 'Feed Monitor' },
    { to: '/entity_logs', label: 'Entity Logs' },
    { to: '/correlation_audit', label: 'Audit' },
    { to: '/ops', label: 'Ops' },
    { to: '/kibana_dashboard', label: 'Kibana' },
    { to: '/insights', label: 'Insights' },
  ]

  return (
    <>
      <div className={styles.header}>
        <div className={styles.navContainer}>
          <nav className={styles.nav}>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}>
                {item.label}
              </NavLink>
            ))}

            <div className={styles.divider} />

            {monitorItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={styles.actions}>
            <div
              className={styles.statusIndicator}
              title={[
                `Workers: ${statusLabels[workerStatus.status]}${
                  workerStatus.lastTaskStatus === 'ERROR' ? ' (last task failed)' : ''
                }`,
                `• Push Queue: ${workerStatus.queue.messages} msgs, ${workerStatus.queue.consumers} consumers`,
                `• DB Check Queue: ${workerStatus.dbCheckQueue.messages} msgs, ${workerStatus.dbCheckQueue.consumers} consumers`,
                workerStatus.lastTaskStatus ? `• Last Task: ${workerStatus.lastTaskStatus}` : '',
              ]
                .filter(Boolean)
                .join('\n')}>
              <span className={`${styles.statusDot} ${styles[workerStatus.status]}`} />
            </div>

            <button
              className={styles.themeToggle}
              onClick={toggleTheme}
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            <div className={styles.profileContainer} ref={dropdownRef}>
              <button
                className={styles.profileBtn}
                onClick={() => setShowDropdown(!showDropdown)}
                title="Account Settings">
                <User size={18} />
                <span className={styles.username}>{user?.username || 'Profile'}</span>
                <ChevronDown size={14} />
              </button>

              {showDropdown && (
                <div className={styles.dropdown}>
                  <a href="/admin/" target="_blank" rel="noopener noreferrer" className={styles.dropdownItem}>
                    <ExternalLink size={16} />
                    <span>Admin</span>
                  </a>
                  <a
                    href="/kibana/app/dev_tools/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.dropdownItem}>
                    <Terminal size={16} />
                    <span>Dev Tools</span>
                  </a>
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      setShowPasswordModal(true)
                      setShowDropdown(false)
                    }}>
                    <Key size={16} />
                    <span>Change Password</span>
                  </button>
                  <button className={`${styles.dropdownItem} ${styles.logout}`} onClick={onLogout}>
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}
    </>
  )
}

export default Header
